import { useCallback, useState } from "react";
import { shiftAwsCron, summarizeAwsCron } from "utils/awsCron";
import { zoneAbbreviation } from "utils/dateUtils";

/**
 * Display mode for run timestamps across the integrations-beta pages:
 * browser-local time, UTC, or the owning integration's client timezone.
 * The choice persists per browser (shared by the list and details pages).
 */
export type TimezoneDisplayMode = "local" | "utc" | "client";

const STORAGE_KEY = "ipTimezoneDisplayMode";

export const ALL_TIMEZONE_DISPLAY_MODES: readonly TimezoneDisplayMode[] = [
  "local",
  "utc",
  "client",
];
/**
 * Local / UTC only: the client-facing pages, where "the client" IS the viewer
 * and a third choice would only confuse (CAL-5777).
 */
export const CLIENT_FACING_TIMEZONE_DISPLAY_MODES: readonly TimezoneDisplayMode[] =
  ["local", "utc"];

function loadMode(): TimezoneDisplayMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (
      stored &&
      (ALL_TIMEZONE_DISPLAY_MODES as readonly string[]).includes(stored)
    ) {
      return stored as TimezoneDisplayMode;
    }
  } catch {}
  return "local";
}

/**
 * The persisted display mode, narrowed to *allowed*: a stored mode outside it
 * (an admin's "client" choice carried into a client-facing page) reads as the
 * first allowed one without rewriting what is stored, so the admin's own
 * preference survives leaving Client View.
 */
export function useTimezoneDisplayMode(
  allowed: readonly TimezoneDisplayMode[] = ALL_TIMEZONE_DISPLAY_MODES,
): [TimezoneDisplayMode, (mode: TimezoneDisplayMode) => void] {
  const [stored, setStored] = useState<TimezoneDisplayMode>(loadMode);
  const update = useCallback((next: TimezoneDisplayMode) => {
    setStored(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {}
  }, []);
  const mode = allowed.includes(stored) ? stored : (allowed[0] ?? "local");
  return [mode, update];
}

/**
 * IANA zone to pass into the date formatters for *mode*; ``undefined`` means
 * browser-local. In client mode a row without a known client zone falls back
 * to local.
 */
export function resolveDisplayZone(
  mode: TimezoneDisplayMode,
  clientTimezone?: string | null,
): string | undefined {
  if (mode === "utc") return "UTC";
  if (mode === "client") return clientTimezone || undefined;
  return undefined;
}

/**
 * Zone hint appended to time column headers ("Started · CDT"). Client mode
 * gets a generic hint - the concrete abbreviation renders next to each
 * timestamp instead.
 */
export function timeHeaderSuffix(mode: TimezoneDisplayMode): string {
  if (mode === "utc") return "UTC";
  if (mode === "client") return "client tz";
  return zoneAbbreviation(new Date().toISOString());
}

export interface FrequencyDisplay {
  items: string[];
  /** false = shown in the CLIENT zone (conversion unavailable) - callers
   * should render the client-zone abbreviation next to the chips. */
  inSelectedZone: boolean;
}

/**
 * Frequency chips for the selected display zone. The backend summary is
 * computed in the integration's own timezone; for Local/UTC modes each
 * active schedule cron is re-expressed in the target zone (rotating weekly
 * day names across midnight). Falls back to the client-zone summary when a
 * cron cannot be shifted faithfully.
 */
export function frequencyItemsForZone(
  frequency: string | null | undefined,
  crons: string[] | null | undefined,
  clientTimezone: string | null | undefined,
  mode: TimezoneDisplayMode,
): FrequencyDisplay {
  const clientItems = frequency ? frequency.split(/;\s*/).filter(Boolean) : [];
  const fallback = { items: clientItems, inSelectedZone: mode === "client" };
  if (mode === "client" || !clientTimezone || !crons?.length) return fallback;

  const target =
    mode === "utc" ? "UTC" : Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (target === clientTimezone)
    return { items: clientItems, inSelectedZone: true };

  const items: string[] = [];
  for (const cron of crons) {
    const shifted = shiftAwsCron(cron, clientTimezone, target);
    if (!shifted) return fallback;
    shifted.forEach((expr) => items.push(summarizeAwsCron(expr)));
  }
  return { items, inSelectedZone: true };
}
